import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChildEdition } from '@/contracts/family';
import type { Story } from '@/contracts/content';
import { Scene } from '@/content/scenes';
import { Button } from '@/design-system/Button';
import { Chip } from '@/design-system/Surfaces';
import { SparkleGlyph } from '@/design-system/Sparkle';
import { cn } from '@/design-system/cn';
import { IconArrow, IconBack, IconSpeaker } from '@/features/studio/Icons';

interface Props {
  story: Story;
  edition: ChildEdition;
  nickname: string;
  /** revision id -> object URL of the child's picture. */
  artUrls: Record<string, string>;
  coverUrl: string | null;
  onExit(): void;
}

/**
 * My Edition (spec §7 C11, §10.1).
 *
 * A private copy of a specific story version in which the child's own finished
 * pictures stand in for the illustrations on approved pages. The published
 * story is never modified, and the original edition stays reachable — there is
 * a toggle on every page that has a personal picture.
 */
export function EditionReader({ story, edition, nickname, artUrls, coverUrl, onExit }: Props) {
  const pages = story.currentVersion.pages;
  // -1 is the cover.
  const [index, setIndex] = useState(-1);
  const [showOriginal, setShowOriginal] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const pending = useRef(-1);

  const slotFor = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of story.currentVersion.editionSlots) map.set(s.pageId, s.caption);
    return map;
  }, [story]);

  const page = index >= 0 ? pages[index] : null;
  const revisionId = page ? edition.slots[page.id] : null;
  const personalUrl = revisionId ? artUrls[revisionId] : null;

  useEffect(() => {
    setShowOriginal(false);
  }, [index]);

  const speak = (text: string) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.82;
    u.onend = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(u);
  };

  const stop = () => {
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  if (pending.current !== index) pending.current = index;

  const go = (delta: number) => {
    const target = Math.min(pages.length - 1, Math.max(-1, pending.current + delta));
    if (target === pending.current) return;
    pending.current = target;
    stop();
    setIndex(target);
  };

  const personalCount = Object.keys(edition.slots).length;

  return (
    <div className="flex h-[100dvh] flex-col bg-paper">
      <header className="flex shrink-0 items-center gap-m px-l py-m">
        <button
          onClick={() => {
            stop();
            onExit();
          }}
          className="flex min-h-touch items-center gap-xs rounded-pebble border-2 border-line bg-card px-m font-display text-[15px] font-semibold text-ink-soft"
        >
          <IconBack aria-hidden />
          <span className="max-[420px]:sr-only">Close</span>
        </button>
        <div className="flex-1 text-center">
          <p className="font-display text-[14px] font-semibold text-ink-faint">
            {index < 0 ? 'Cover' : `Page ${index + 1} of ${pages.length}`}
          </p>
        </div>
        <Chip tone="magic" icon={<SparkleGlyph className="h-3 w-3" />}>
          {personalCount} of yours
        </Chip>
      </header>

      <main className="flex min-h-0 flex-1 flex-col gap-l overflow-y-auto px-l pb-l lg:flex-row lg:items-center lg:gap-xl">
        {index < 0 ? (
          /* ---------------- Personal cover ---------------- */
          <div className="mx-auto w-full max-w-2xl text-center">
            <div className="overflow-hidden rounded-blob border-[3px] border-line shadow-cut-lg">
              <div className="relative">
                {coverUrl ? (
                  <div className="grid aspect-[3/2] w-full place-items-center bg-white p-xl">
                    <img src={coverUrl} alt="Your cover picture" className="max-h-full w-auto" />
                  </div>
                ) : (
                  <Scene id={story.coverSceneId} className="aspect-[3/2] w-full" title={story.title} />
                )}
              </div>
              <div className="border-t-2 border-line bg-card p-xl">
                <h1 className="font-display text-[26px] font-semibold text-ink sm:text-[32px]">
                  {story.title}
                </h1>
                <p className="mt-s font-story text-[18px] text-ink-soft">
                  A copy made by {nickname}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <figure className="lg:flex-[1.15]">
              <div className="overflow-hidden rounded-blob border-2 border-line shadow-cut-lg">
                {personalUrl && !showOriginal ? (
                  <div className="grid aspect-[3/2] w-full place-items-center bg-white p-l">
                    <img
                      src={personalUrl}
                      alt={`${slotFor.get(page!.id) ?? 'Your picture'}`}
                      className="max-h-full w-auto"
                    />
                  </div>
                ) : (
                  <Scene id={page!.sceneId} title={page!.alt} className="aspect-[3/2] w-full" />
                )}
              </div>

              {personalUrl ? (
                <figcaption className="mt-m flex flex-wrap items-center justify-between gap-s">
                  <span className="font-display text-[14px] font-semibold text-magic-deep">
                    {showOriginal ? 'The original picture' : slotFor.get(page!.id)}
                  </span>
                  {/* The original published edition stays accessible (§10.1). */}
                  <button
                    onClick={() => setShowOriginal((v) => !v)}
                    className="min-h-touch rounded-pebble border-2 border-line bg-card px-m font-display text-[14px] font-semibold text-ink-soft"
                  >
                    {showOriginal ? 'Show mine' : 'Show the original'}
                  </button>
                </figcaption>
              ) : null}
            </figure>

            <div className="lg:flex-1">
              <p className="font-story text-[24px] leading-[1.5] text-ink sm:text-[28px]">
                {page!.text}
              </p>
              <button
                onClick={() => (speaking ? stop() : speak(page!.text))}
                className="mt-l inline-flex min-h-touch items-center gap-s rounded-pebble border-2 border-line bg-card px-l font-display text-[15px] font-semibold text-ink-soft"
              >
                <IconSpeaker aria-hidden />
                {speaking ? 'Stop reading' : 'Read this page to me'}
              </button>
            </div>
          </>
        )}
      </main>

      <footer className="flex shrink-0 items-center gap-m border-t-2 border-line bg-card px-l py-m pb-[max(12px,env(safe-area-inset-bottom))]">
        <Button variant="outline" size="lg" onClick={() => go(-1)} disabled={index === -1} icon={<IconBack />}>
          Back
        </Button>
        <div className="flex-1" />
        <Button
          variant={index === pages.length - 1 ? 'outline' : 'primary'}
          size="lg"
          onClick={() => (index === pages.length - 1 ? onExit() : go(1))}
          iconRight={index === pages.length - 1 ? undefined : <IconArrow />}
        >
          {index === pages.length - 1 ? 'The end' : 'Next'}
        </Button>
      </footer>

      <div aria-hidden className={cn('h-0 overflow-hidden')} />
    </div>
  );
}
